import { BaseRpcClient } from './BaseRpcClient.js'

/**
 * RPC Client for the Hub (Merchant Dashboard) application.
 * No method prefix - Hub procedures use 'hub.' namespace directly.
 *
 * Authentication, MFA helpers, and challenge/setup-token routing all
 * live on `BaseRpcClient` so admin / hub / customer / app share the
 * same surface and only differ in:
 *   - which localStorage key holds the access token
 *   - the unauthorized-redirect path
 *   - the `app` ability scope passed to `auth@login`
 */
export class HubRpcClient extends BaseRpcClient {
  /**
   * @param {Partial<import('./BaseRpcClient.js').RpcClientConfig> & { app?: string }} [config]
   */
  constructor(config = {}) {
    super({
      baseURL: config.baseURL || '/rpc',
      ...config,
    })

    this._version = '1.0.0-hub'
    this._app = config.app || 'hub'
  }

  /**
   * Login with hub app abilities.
   *
   * The response can be one of:
   *   - { access_token, ... } → straight login, store the token
   *   - { status: 'mfa_challenge', challenge_token, methods, ... } → MFA verify required
   *   - { status: 'mfa_setup_required', setup_token, ... } → forced setup
   *   - { status: 'password_change_required', change_token, ... } → the
   *     password was set by an administrator and must be replaced (#1198).
   *     Raised AFTER any MFA step, so reaching it means both factors passed.
   *
   * Callers inspect the `status` field; only the straight-login branch
   * sets a token here so MFA flows don't leak a half-authenticated state
   * into subsequent requests.
   *
   * @param {string} email
   * @param {string} password
   * @returns {Promise<import('@monorepo/shared-types').AuthResponse>}
   */
  async login(email, password) {
    // Clear any stale token BEFORE the call so an `mfa_challenge` /
    // `mfa_setup_required` response can't piggy-back on an old session's
    // Authorization header for the follow-up `mfa@*` calls.
    this.clearAuthToken()

    const result = await this.call('auth@login', {
      email,
      password,
      app: this._app,
    })
    if (result?.access_token) {
      this.setAuthToken(result.access_token)
    }
    return result
  }

  /**
   * @returns {Promise<void>}
   */
  async logout() {
    await this.call('auth@logout')
    this.clearAuthToken()
  }

  /** Get current user info */
  async me() {
    return this.call('auth@me')
  }

  /** Refresh the authentication token */
  async refreshToken() {
    const result = await this.call('auth@refresh')
    this.setAuthToken(result.access_token)
    return result
  }

  // Token storage with hub-specific key
  getAuthToken() {
    return localStorage.getItem('hub_token')
  }

  setAuthToken(token) {
    localStorage.setItem('hub_token', token)
  }

  clearAuthToken() {
    localStorage.removeItem('hub_token')
  }

  /**
   * Handle unauthorized responses (401 errors).
   * Clears persisted auth data and signals the rest of the app via a
   * `session-expired` event so it can wipe in-memory state.
   */
  handleUnauthorized() {
    this.clearAuthToken()
    localStorage.removeItem('hub_user')
    localStorage.removeItem('hub_roles')
    localStorage.removeItem('hub_permissions')

    if (typeof window !== 'undefined' && window.location) {
      const currentPath = window.location.pathname + window.location.search
      if (currentPath !== '/login' && !currentPath.startsWith('/login')) {
        sessionStorage.setItem('hub_redirectAfterLogin', currentPath)
        window.dispatchEvent(new CustomEvent('session-expired'))
      }
    }
  }
}
